import { ApolloError, useMutation, useQuery } from "@apollo/client";
import { Toast } from "@aurem/ui";
import { useI18n } from "providers/I18nProvider";
import {
  EnvironmentConfigInput,
  EnvironmentTheme,
  FindEnvironmentConfigQuery,
  gql,
  LogoVariant,
} from "types";
import { EnvironmentFormSchema } from "../types/environment.form.types";

export const FIND_ENVIRONMENT_CONFIG = gql(/* GraphQL */ `
  query FindEnvironmentConfig {
    findEnvironmentConfig {
      id
      name
      logoUrlRegular
      logoUrlInverse
      logoUrlEmail
      logoUrlSymbol
      nameAr
      privacyNoticeEn
      privacyNoticeAr
      feelingLostUrlEn
      feelingLostUrlAr
      themeName
      firstLogin
      kycProvider
      blockedCitizenships
      unitsDecimalPlaces
      navDecimalPlaces
      chatwootToken
      inactivityTimeoutMinutes
    }
  }
`);

const FIND_CHATWOOT_SECRET_KEY = gql(/* GraphQL */ `
  query FindChatwootSecretKey {
    findChatwootSecretKey
  }
`);

const UPSERT_ENVIRONMENT_CONFIG = gql(/* GraphQL */ `
  mutation UpsertEnvironmentConfig($data: EnvironmentConfigInput!) {
    upsertEnvironmentConfig(data: $data) {
      name
      nameAr
      privacyNoticeEn
      privacyNoticeAr
      feelingLostUrlEn
      feelingLostUrlAr
      themeName
      firstLogin
      kycProvider
      blockedCitizenships
      chatwootToken
    }
  }
`);

const UPSERT_FIRST_LOGIN_CONFIG = gql(/* GraphQL */ `
  mutation UpsertFirstLogin($value: Boolean!) {
    upsertFirstLogin(value: $value)
  }
`);

const UPLOAD_ENVIRONMENT_LOGO = gql(/* GraphQL */ `
  mutation UploadEnvironmentLogo($logo: Upload!, $variant: LogoVariant!) {
    uploadEnvironmentLogo(logo: $logo, variant: $variant) {
      name
    }
  }
`);

const DELETE_ENVIRONMENT_LOGO = gql(/* GraphQL */ `
  mutation DeleteEnvironmentLogo($variant: LogoVariant!) {
    deleteEnvironmentLogo(variant: $variant) {
      name
    }
  }
`);

type ConfigType = FindEnvironmentConfigQuery["findEnvironmentConfig"];

export function getDefaultValues(
  config: ConfigType,
  chatwootSecretKey = "",
): EnvironmentFormSchema {
  return {
    name: config?.name ?? "",
    themeName: config?.themeName ?? EnvironmentTheme.Black,
    nameAr: config?.nameAr ?? "",
    privacyNoticeEn: config?.privacyNoticeEn ?? "",
    privacyNoticeAr: config?.privacyNoticeAr ?? "",
    feelingLostUrlEn: config?.feelingLostUrlEn ?? "",
    feelingLostUrlAr: config?.feelingLostUrlAr ?? "",
    chatwootToken: config?.chatwootToken ?? "",
    chatwootSecretKey,
  };
}

export const useEnvironmentConfig = () => {
  const { data, loading } = useQuery(FIND_ENVIRONMENT_CONFIG);

  return {
    config: data?.findEnvironmentConfig,
    loading,
  };
};

export const useChatwootSecretKey = () => {
  const { data, loading } = useQuery(FIND_CHATWOOT_SECRET_KEY);

  return {
    secretKey: data?.findChatwootSecretKey,
    loading,
  };
};

export const useUpsertEnvironmentConfig = () => {
  const [upsertEnvironmentConfig, { loading }] = useMutation(
    UPSERT_ENVIRONMENT_CONFIG,
    {
      refetchQueries: [
        { query: FIND_ENVIRONMENT_CONFIG },
        { query: FIND_CHATWOOT_SECRET_KEY },
        "Environment",
      ],
    },
  );

  const [upsertFirstLogin, { loading: firstLoginLoading }] = useMutation(
    UPSERT_FIRST_LOGIN_CONFIG,
    {
      refetchQueries: [{ query: FIND_ENVIRONMENT_CONFIG }],
      awaitRefetchQueries: true,
    },
  );

  const { t } = useI18n();

  const upsertEnvironment = async (
    values: EnvironmentConfigInput,
    withToast = true,
  ): Promise<boolean> => {
    try {
      await upsertEnvironmentConfig({
        variables: {
          data: {
            ...values,
            themeName: values.themeName as EnvironmentTheme,
          },
        },
      });

      if (withToast)
        Toast.success({
          title: t("apps.admin.common.changesSavedSuccessfully"),
        });

      return true;
    } catch (e) {
      Toast.error({
        title: (e as ApolloError).message,
      });

      return false;
    }
  };

  const upsertFirstLoginFlag = async (value: boolean) => {
    try {
      await upsertFirstLogin({
        variables: {
          value,
        },
      });
      Toast.success({
        title: t("apps.admin.common.changesSavedSuccessfully"),
      });
      return true;
    } catch (e) {
      Toast.error({
        title: (e as ApolloError).message,
      });
      return false;
    }
  };

  return {
    upsertEnvironment,
    upsertFirstLoginFlag,
    loading,
    firstLoginLoading,
  };
};

export function useManageEnvironmentLogo() {
  const [uploadEnvironmentLogo, { loading: uploading }] = useMutation(
    UPLOAD_ENVIRONMENT_LOGO,
    {
      refetchQueries: [{ query: FIND_ENVIRONMENT_CONFIG }, "Environment"],
    },
  );
  const [deleteEnvironmentLogo, { loading: deleting }] = useMutation(
    DELETE_ENVIRONMENT_LOGO,
    {
      refetchQueries: [{ query: FIND_ENVIRONMENT_CONFIG }, "Environment"],
    },
  );
  async function uploadLogo(logo: File, variant: LogoVariant) {
    try {
      await uploadEnvironmentLogo({
        variables: {
          logo,
          variant,
        },
      });
    } catch (err) {
      Toast.error({
        title: (err as ApolloError).message,
      });
    }
  }

  async function deleteLogo(variant: LogoVariant) {
    try {
      await deleteEnvironmentLogo({
        variables: {
          variant,
        },
      });
    } catch (e) {
      Toast.error({
        title: (e as ApolloError).message,
      });
    }
  }

  return { uploadLogo, uploading, deleteLogo, deleting };
}
